import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Grid } from '@sorare/core/src/atoms/layout/grid';
import { HeadlineL } from '@sorare/core/src/atoms/typography';
import { messages } from '@sorare/core/src/components/rewards/RewardBoxDialog/utils';

import {
  REWARD_BOX_CONTENT_FRAGMENT,
  getFallbacks,
  useFormatBox,
} from 'components/rewards/RewardBoxDialog/utils';

import type { RewardBoxFallback_ProbabilisticBundle } from './__generated__/index.graphql';

type Props = {
  rewardBoxes: RewardBoxFallback_ProbabilisticBundle[];
};

export const RewardBoxFallback = ({ rewardBoxes }: Props) => {
  const fallbacks: React.JSX.Element[] = getFallbacks(
    rewardBoxes.flatMap(b => b.items)
  );
  const boxes = useFormatBox(rewardBoxes);
  const [hasOpenedAll, setHasOpenedAll] = useState(false);
  const [loading, setLoading] = useState(false);

  if (rewardBoxes.length === 0) {
    return null;
  }

  return (
    <Vertical center gap={4}>
      {!hasOpenedAll ? (
        <LoadingButton
          size="large"
          loading={loading}
          onClick={async () => {
            setLoading(true);
            // claim all openable boxes
            await Promise.all(
              boxes.filter(b => b.isOpenable).map(async b => b.onClaim())
            );
            setLoading(false);
            setHasOpenedAll(true);
          }}
        >
          <FormattedMessage
            id="RewardBox.openAll"
            defaultMessage="{nb, plural, one {Open box} other {Open {nb} boxes}}"
            values={{ nb: rewardBoxes.filter(b => !b.opened).length }}
          />
        </LoadingButton>
      ) : (
        <Vertical>
          <HeadlineL as="h1">
            <FormattedMessage {...messages.hereWhatYouWon} />
          </HeadlineL>
          <Grid gap={2}>
            {fallbacks.map((fallback, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={index}>{fallback}</li>
            ))}
          </Grid>
        </Vertical>
      )}
    </Vertical>
  );
};

RewardBoxFallback.fragments = {
  ProbabilisticBundle: gql`
    fragment RewardBoxFallback_ProbabilisticBundle on ProbabilisticBundle {
      id
      ...useFormatBox_ProbabilisticBundle
      isOpenable
      items {
        id
        ...RewardBoxContent_ProbabilisticBundleSlotInterface
      }
    }
    ${useFormatBox.fragments.ProbabilisticBundle}
    ${REWARD_BOX_CONTENT_FRAGMENT}
  ` as TypedDocumentNode<RewardBoxFallback_ProbabilisticBundle>,
};
